/**
 * Weather of the world (pure, unit tested): four states (clear, overcast, rain, fog) and `auto`,
 * which cycles through them on a deterministic schedule. What the scene draws is a continuous
 * mix that eases toward the state's targets: clouds and fog in about 20 s, rain in 8 s, while
 * the ground gets wet over a minute of rain and dries over several minutes after it (puddles
 * last longest). Shaders read the mix through `lookUniforms` (`WeatherDriver`).
 */

export const WEATHER_STATES = ["clear", "overcast", "rain", "fog"] as const;
export type WeatherState = (typeof WEATHER_STATES)[number];
export type WeatherSetting = WeatherState | "auto";
export const WEATHER_SETTINGS: readonly WeatherSetting[] = ["auto", ...WEATHER_STATES];

export interface WeatherMix {
  /** Cloud cover 0..1 (sky, light), rain 0..1, fog 0..1, wind 0..1. */
  cloud: number;
  rain: number;
  fog: number;
  wind: number;
  /** Surface wetness and standing water, 0..1 (they lag behind the rain). */
  wet: number;
  puddle: number;
}

export const TARGETS: Readonly<Record<WeatherState, Omit<WeatherMix, "wet" | "puddle">>> = {
  clear: { cloud: 0.18, rain: 0, fog: 0, wind: 0.3 },
  overcast: { cloud: 0.85, rain: 0, fog: 0.15, wind: 0.5 },
  rain: { cloud: 1, rain: 1, fog: 0.3, wind: 0.75 },
  fog: { cloud: 0.6, rain: 0, fog: 1, wind: 0.1 },
};

/** Seconds to reach about 63 % of a change (first-order easing). */
const TAU = { cloud: 7, rain: 3, fog: 7, wind: 5 } as const;
/** Wetting while it rains (per second), drying after (per second). */
const WET_UP = 1 / 45;
const WET_DOWN = 1 / 240;
const PUDDLE_UP = 1 / 80;
const PUDDLE_DOWN = 1 / 420;

export const isWeatherSetting = (v: unknown): v is WeatherSetting =>
  v === "auto" || (WEATHER_STATES as readonly unknown[]).includes(v);

/** The mix fully settled on a state (a fresh load, `?weather=`). Rain starts with wet ground. */
export function settledMix(state: WeatherState): WeatherMix {
  const t = TARGETS[state];
  return { ...t, wet: state === "rain" ? 1 : 0, puddle: state === "rain" ? 0.8 : 0 };
}

/** Advances the mix toward a state by `dt` seconds. Pure: returns a new mix. */
export function stepWeather(m: WeatherMix, state: WeatherState, dt: number): WeatherMix {
  const t = TARGETS[state];
  const ease = (a: number, b: number, tau: number) => b + (a - b) * Math.exp(-dt / tau);
  const raining = m.rain > 0.35;
  const wet = raining
    ? Math.min(1, m.wet + dt * WET_UP * (0.5 + m.rain))
    : Math.max(0, m.wet - dt * WET_DOWN * (state === "clear" ? 1.5 : 1));
  const puddle = raining
    ? Math.min(1, m.puddle + dt * PUDDLE_UP * m.rain)
    : Math.max(0, m.puddle - dt * PUDDLE_DOWN);
  return {
    cloud: ease(m.cloud, t.cloud, TAU.cloud),
    rain: ease(m.rain, t.rain, TAU.rain),
    fog: ease(m.fog, t.fog, TAU.fog),
    wind: ease(m.wind, t.wind, TAU.wind),
    wet,
    puddle: Math.min(puddle, wet),
  };
}

/** True while the mix still moves noticeably toward the state (the frame loop keeps going). */
export function settling(m: WeatherMix, state: WeatherState): boolean {
  const t = TARGETS[state];
  const far =
    Math.abs(m.cloud - t.cloud) > 0.004 ||
    Math.abs(m.rain - t.rain) > 0.004 ||
    Math.abs(m.fog - t.fog) > 0.004;
  const drying = state !== "rain" && (m.wet > 0.001 || m.puddle > 0.001);
  const wetting = state === "rain" && (m.wet < 0.999 || m.puddle < 0.999);
  return far || drying || wetting;
}

/** Length of one weather spell of the automatic cycle (seconds). */
export const AUTO_SPELL_S = 20 * 60;

function hash(n: number): number {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * The automatic cycle: a spell every 20 minutes of the viewer's clock, mostly clear, sometimes
 * overcast, now and then rain or fog; rain follows overcast (no rain out of a clear sky).
 */
export function autoState(epochSeconds: number): WeatherState {
  const spell = Math.floor(epochSeconds / AUTO_SPELL_S);
  const pick = (k: number): WeatherState => {
    const h = hash(k);
    return h < 0.5 ? "clear" : h < 0.75 ? "overcast" : h < 0.9 ? "rain" : "fog";
  };
  const now = pick(spell);
  if (now === "rain" && pick(spell - 1) === "clear") return "overcast";
  return now;
}

export function stateOf(setting: WeatherSetting, epochSeconds: number): WeatherState {
  return setting === "auto" ? autoState(epochSeconds) : setting;
}

/** `?weather=clear|overcast|rain|fog|auto` fixes the weather (screenshots, tests). */
export function weatherFromQuery(
  search: string = globalThis.location?.search ?? "",
): WeatherSetting | null {
  const m = /[?&]weather=(auto|clear|overcast|rain|fog)(?:&|$)/.exec(search);
  return m ? (m[1] as WeatherSetting) : null;
}

/** A word-start match that also works for Turkish letters (`\b` is ASCII-only). */
const word = (pattern: string): RegExp => new RegExp(`(?<![\\p{L}])(?:${pattern})`, "u");

const RAIN = word("rain|rainy|raining|drizzle|storm|yağmur|yagmur|sağanak|yağdır|yagdir");
const FOG = word("fog|foggy|mist|misty|haze|sis|sisli|pus");
const CLOUD = word("overcast|cloud|cloudy|grey sky|gray sky|bulut|bulutlu|kapalı|kapali");
const CLEAR = word(
  "clear|sunny|sunshine|fair weather|güneş|gunes|güneşli|açık hava|acik hava|ayaz",
);
const AUTO = word("auto|automatic|otomatik|kendiliğinden|doğal|dogal");
const WEATHER_CUE = word("weather|hava|sky|gökyüzü|gokyuzu");
const SET_WORDS = word(
  "set|make|let|switch|change|turn|start|stop|yap|ayarla|olsun|getir|başlat|baslat|dursun|kes|yağdır|yagdir",
);
const STOP_RAIN = word(
  "stop (?:the )?rain|yağmuru (?:durdur|kes)|yagmuru (?:durdur|kes)|yağmur dursun|yagmur dursun",
);

/**
 * A typed or spoken weather command ("yağmur yağdır", "make it rain", "sis olsun", "set the
 * weather to clear", "hava otomatik"), or null when the text is something else.
 */
export function parseWeatherIntent(text: string): WeatherSetting | null {
  const s = text.trim().toLowerCase();
  if (s.length === 0 || s.length > 80) return null;
  if (STOP_RAIN.test(s)) return "clear";
  const rain = RAIN.test(s);
  const fog = FOG.test(s);
  const cloud = CLOUD.test(s);
  const clear = CLEAR.test(s);
  const auto = AUTO.test(s) && WEATHER_CUE.test(s);
  const kinds = [rain, fog, cloud, clear, auto].filter(Boolean).length;
  if (kinds !== 1) return null;
  const words = s.split(/\s+/).length;
  // A command, or short enough to be one ("yağmur", "sisli hava"): "rain check on that meeting"
  // and longer sentences go to the assistant.
  if (!SET_WORDS.test(s) && !WEATHER_CUE.test(s) && words > 2) return null;
  if (words > 8) return null;
  if (rain) return "rain";
  if (fog) return "fog";
  if (cloud) return "overcast";
  if (clear) return "clear";
  return "auto";
}
